(ns io.daskul.tutorial.webapp.views.calendar
  "Sessions calendar — a day view (SMS-37) and a week view (SMS-153),
   toggled via a Day/Week control. The active view lives in the URL
   (?view=week; default day), so it is shareable and survives a refresh.

   Sessions are absolutely-positioned blocks whose top + height come from
   `:start-time` / `:end-time` (no slot snapping; vacancy = whitespace). The
   week view lays them out per day-column (cal/layout-week) over the same
   7am–9pm axis and scrolls horizontally on a phone (day view stays the
   mobile default).

   Slice 1 surfaces real Sessions for the current day with prev/today/next
   navigation and a primary 'New Session' button that routes to the
   generic Session create form.

   Slice 2 surfaces the attendee count as a Mantine Badge on each block
   (consumes :attendees-count enriched on the Session list response by
   the after-read hook).

   #512: a block's colour comes from :attendee-kinds — free trial, make-up,
   or paid — not from its program name. The colour is the card's surface and
   spine; all its text stays the theme grey. See views/calendar/kinds."
  (:require
   [clojure.string :as str]
   [uix.core :refer [defui $ use-effect use-state use-callback]]
   [uix.re-frame :as urf]
   [io.daskul.webapp.api.http :as api]
   [io.daskul.webapp.calendar :as cal]
   [io.daskul.tutorial.webapp.views.calendar.kinds :as kinds]
   [io.daskul.tutorial.webapp.domain.active-context.subs :as ac.subs]
   [io.daskul.tutorial.webapp.router :as router]
   [io.daskul.webapp.ui :as ui]
   [io.daskul.webapp.ui.icons :as icons]
   [io.daskul.webapp.widgets.layout.slide-over :as slide-over]
   [io.daskul.tutorial.webapp.views.session-edit :as session-edit]))

;; ============================================================================
;; Tokens — Calendar layout
;; ============================================================================
;;
;; Visual contract: SMS-37 house-style mockup
;; (docs/superpowers/mockups/2026-05-30-sms-37-calendar-day-view/house-style.html).
;; The actual app theme provides the type, color, radius, and shadow tokens;
;; the calendar adds positional tokens (px-per-minute, day window, rail width).

(def ^:private day-start-hour 7)
(def ^:private day-end-hour 21) ;; 9 pm
(def ^:private hours-in-window (- day-end-hour day-start-hour))
;; Tuned for whole-day visibility with comfortable block density: at
;; 1.2/1.3 the full 7am–9pm window stays close to one viewport (~1000–
;; 1090px tall) while 60/90-min blocks have enough room for the time +
;; program + attendee badge. Short (≤30-min) blocks switch to a single-
;; line compact layout in session-block (rather than relying on height).
(def ^:private px-per-min-mobile 1.2)
(def ^:private px-per-min-desktop 1.3)
(def ^:private rail-w-mobile 56)
(def ^:private rail-w-desktop 80)

;; Each session block is capped to a comfortable card width. Single
;; (non-overlapping) blocks anchor left and stop at this width — they
;; needn't span the whole page. Concurrent blocks split the *available*
;; lane evenly, each still bounded by this cap. So:
;;
;;   1 block          → up to 440px (card-like)
;;   2 concurrent     → each up to 440px → splits if lane > 880, else 50/50
;;   3 concurrent     → each up to 440px → splits if lane > 1320, else 33/33/33
;;
;; CSS does this in one shot via min(slot-share, cap).
(def ^:private block-max-w 440)

;; Week-grid tokens (SMS-153). Seven day-columns share the day view's
;; 7am–9pm axis + px-per-min. Each column has a minimum width so that on a
;; phone the 7-column row overflows its container and scrolls horizontally
;; (day view stays the mobile default); on desktop the columns flex-grow to
;; fill. The time rail + the day-name header row are sticky so they stay
;; visible while scrolling.
(def ^:private week-col-min-w-mobile 120)
(def ^:private week-col-min-w-desktop 150)
(def ^:private week-header-h 52)

;; ============================================================================
;; Day window helpers (pure)
;; ============================================================================

;; Wall-date helpers (format-ymd, week math, per-day overlap layout) live in
;; the shared, unit-tested logic layer io.daskul.webapp.calendar (cal/*).

;; #590 — wall-clock time helpers. The API serves :start-time / :end-time as
;; naive "HH:MM:SS" (the school's local wall clock), so a block's position and
;; labels are pure string arithmetic — none of the Intl/tz machinery the old
;; instant path needed. A session at 09:00 lands on the 9am row for every
;; viewer, in any browser tz.

(defn- time->minutes
  "Minutes since midnight for a wall-clock \"HH:MM\" / \"HH:MM:SS\" string."
  [t]
  (let [[h m] (str/split t #":")]
    (+ (* 60 (js/parseInt h 10)) (js/parseInt m 10))))

(defn- duration-minutes
  "Whole minutes between two same-day wall-clock times."
  [start-t end-t]
  (max 0 (- (time->minutes end-t) (time->minutes start-t))))

(defn- format-time-12h
  "Render a wall-clock \"HH:MM:SS\" as '9:00 am'."
  [t]
  (let [mins (time->minutes t)
        h24 (quot mins 60)
        m (mod mins 60)
        ampm (if (>= h24 12) "pm" "am")
        h12 (let [h (mod h24 12)] (if (zero? h) 12 h))]
    (str h12 ":" (cal/two-digit m) " " ampm)))

(defn- format-time-range
  [start-t end-t]
  (str (format-time-12h start-t) " – " (format-time-12h end-t)))

;; ----------------------------------------------------------------------------
;; School-tz calendar-field helper (SMS-49). Session TIMES no longer need this
;; (#590 serves wall-clock), but "today" still does: the Today button must jump
;; to the SCHOOL's today, which depends on the school's tz. today-in-tz wraps
;; Intl.DateTimeFormat to read Y-M-D in a given IANA tz.
;; ----------------------------------------------------------------------------

(defn- ^js parts->lookup
  "Convert Intl.DateTimeFormat#formatToParts result to {type -> value}.

   SMS-49 helper. Used by the tz-aware formatters to pluck out :hour /
   :minute / :year / :month / :day in a target IANA tz."
  [^js parts]
  (let [out #js {}]
    (.forEach parts (fn [part] (aset out (.-type part) (.-value part))))
    out))

(defn- today-in-tz
  "Return a js/Date whose YYYY-MM-DD components match *today* in the
   given IANA tz (constructed at local-midnight to match the calendar's
   existing `set-date!` shape). Used by the Today button so it always
   jumps to school's-today, not browser's-today."
  [tz]
  (let [fmt (js/Intl.DateTimeFormat.
             "en-CA" ;; en-CA renders YYYY-MM-DD ordering, easy to parse.
             #js {:timeZone tz
                  :year "numeric"
                  :month "2-digit"
                  :day "2-digit"})
        parts (.formatToParts fmt (js/Date.))
        lookup (parts->lookup parts)
        y (js/parseInt (aget lookup "year") 10)
        m (js/parseInt (aget lookup "month") 10)
        d (js/parseInt (aget lookup "day") 10)]
    (js/Date. y (dec m) d 0 0 0 0)))

(defn- browser-tz
  "IANA tz string for the browser's current locale."
  []
  (.. (js/Intl.DateTimeFormat.) resolvedOptions -timeZone))

(defn- day-label
  "Monday, 26 May 2026 — Sora h2."
  [^js d]
  (.toLocaleDateString d "en-US"
                       #js {:weekday "long" :day "numeric" :month "short" :year "numeric"}))

(defn- hour-label
  [h]
  (cond
    (zero? h) [12 "am"]
    (< h 12) [h "am"]
    (= h 12) [12 "pm"]
    :else [(- h 12) "pm"]))

;; ============================================================================
;; Layout-only components (no data fetching, no callbacks beyond props)
;; ============================================================================

(defui hour-rail-tick [{:keys [hour px-per-min rail-w]}]
  (let [[n suffix] (hour-label hour)
        top-px (* (- (* hour 60) (* day-start-hour 60)) px-per-min)]
    ($ :div {:style {:position "absolute"
                     :left rail-w
                     :right 18
                     :top top-px
                     :height 1
                     :backgroundColor "var(--mantine-color-gray-2)"}}
       ($ :span {:style {:position "absolute"
                         :left (str "calc(-1 * " rail-w "px + 14px)")
                         :top -7
                         :fontFamily "Plus Jakarta Sans, sans-serif"
                         :fontSize 11
                         :fontWeight 500
                         :color "var(--mantine-color-gray-6)"
                         :fontVariantNumeric "tabular-nums"
                         :whiteSpace "nowrap"}}
          n " "
          ($ :span {:style {:color "var(--mantine-color-gray-5)"
                            :marginLeft 3
                            :fontWeight 400}}
             suffix)))))

(defui kind-tag
  "#512 — the card's kind in words, so colour is never the only carrier of it.

   A badge, like every other categorical label in the app — the Sessions list
   marks state with one, the Attendees table marks Lead vs Enrollment with
   one. A bare uppercase run of text would have been this calendar inventing
   a private idiom for the same job.

   White fill, grey text, no hue of its own. The card surface already says
   amber; a coloured chip would say it a second time, and would sit oddly
   next to the semantically-coloured state badges elsewhere. White works
   because a tag only ever appears on a trial or make-up card — a paid or
   empty card has none — so the chip always lands on a tinted background with
   something to contrast against.

   Grey text rather than the kind's hue for the same reason the rest of the
   card is grey: it left the tag looking like the same class of thing as the
   student names beside it, which are people, not categories.

   :title carries the spelled-out kind list, which is the only thing that
   explains the `+` on a mixed card."
  [{:keys [tag]}]
  ($ ui/badge {:size "sm"
               :radius "sm"
               ;; Tighter than the badge default. A chip costs width the plain
               ;; label didn't — "Makeup" went 41px → 62px — and width is the
               ;; scarce thing on this card. Mantine's badge truncates its own
               ;; label rather than overflowing, so the failure is silent: at
               ;; 67px "TRIAL" was rendering clipped INSIDE a chip that looked
               ;; like it fit. 6px buys that back.
               :px 6
               :title (:label tag)
               :style {:backgroundColor "#ffffff"
                       :color kinds/card-text-color
                       ;; Never the thing that gives way: it is a handful of
                       ;; characters, and it is the part a colour-blind reader
                       ;; is relying on.
                       :flexShrink 0}}
     (:text tag)))

(defui session-block
  "One Session positioned in the time grid. Pure visual; click goes to the
   framework-generic Session edit page.

   `:col` / `:col-count` come from assign-overlap-layout — they split
   the lane width evenly across concurrent sessions. A non-overlapping
   block renders full-width (col=0, col-count=1).

   Position + labels come from the session's naive wall clock (:start-time /
   :end-time), so they need no tz — the block lands on the same row for every
   viewer (#590)."
  [{:keys [session px-per-min on-click]}]
  ;; branch-name is intentionally omitted from the block — the active
  ;; branch is already shown in the global context bar; repeating it on
  ;; every session is noise. normalize-session still extracts it for
  ;; future use (tooltip / multi-branch overlay).
  (let [{:keys [id start-time end-time program attendee-count attendees-preview
                attendee-kinds col col-count]} session
        col (or col 0)
        col-count (max (or col-count 1) 1)
        start-min (time->minutes start-time)
        dur-min (max 30 (duration-minutes start-time end-time))
        top-px (* (- start-min (* day-start-hour 60)) px-per-min)
        height-px (* dur-min px-per-min)
        ;; #512 — the card is coloured by WHO is in it, not by which program
        ;; it teaches. :attendee-kinds arrives precedence-ordered, so `first`
        ;; is the dominant kind (nil on a session with no live attendees).
        card-colors (kinds/kind->card-colors (first attendee-kinds))
        ;; …and says so in words. nil on a paid or empty card — see
        ;; calendar.kinds/kinds->tag for why those two stay unlabelled.
        tag (kinds/kinds->tag attendee-kinds)
        program-name (or (:name program) "Session")
        ;; Each block claims a slot of width min(lane/col-count, max).
        ;; - Single block → up to max, anchored left.
        ;; - Concurrent → each slot ≤ max, splitting evenly if smaller.
        ;; 4px gutter on each side keeps adjacent blocks from kissing.
        col-w-pct (/ 100.0 col-count)
        ;; Compute the attendee label once (same logic both layouts).
        attendee-label (when (and attendee-count (pos? attendee-count))
                         (if (and (<= attendee-count 2) (seq attendees-preview))
                           (str/join ", " attendees-preview)
                           (str attendee-count " student"
                                (when-not (= attendee-count 1) "s"))))
        ;; Short blocks (≤30-min) only have room for a single line of
        ;; content. Switch to an inline layout so the program name stays
        ;; visible without forcing a taller, scroll-heavy grid.
        compact? (<= dur-min 30)
        ;; #512 — the kind in the card's accessible name, not only in its
        ;; pixels. The visible tag is the primary non-colour carrier, but it
        ;; can be squeezed out: below ~61px (a 41px tag plus the card's
        ;; padding) there is physically nowhere to put it, and three
        ;; concurrent sessions in a week column already land near there. This
        ;; is what keeps the kind available when that happens — and it is what
        ;; a screen-reader user gets either way, since neither the colour nor
        ;; an uppercase micro-label ever reached them.
        aria-label (->> [(:label tag)
                         program-name
                         (format-time-range start-time end-time)
                         attendee-label]
                        (remove nil?)
                        (str/join ", "))]
    ($ :article
       {:onClick #(on-click id)
        :aria-label aria-label
        :style {:position "absolute"
                :left (str "calc(" col " * min(" col-w-pct "%, " block-max-w "px) + 4px)")
                :width (str "calc(min(" col-w-pct "%, " block-max-w "px) - 8px)")
                :top top-px
                :height height-px
                :padding (if compact? "4px 10px" "8px 12px")
                :backgroundColor (:bg card-colors)
                ;; Four longhands, not `border` + a `borderLeft` override. This
                ;; map has 19 keys, so ClojureScript compiles it to a
                ;; PersistentHashMap and hands React the properties in hash
                ;; order — whereupon the shorthand lands last and erases the
                ;; override. `border-left` was absent from the rendered style
                ;; attribute entirely; the spine has never drawn since the
                ;; calendar shipped, and it went unnoticed while the two
                ;; colours were adjacent shades of one Mantine hue. Longhands
                ;; don't overlap, so their order stops mattering.
                :borderTop (str "1px solid " (:border card-colors))
                :borderRight (str "1px solid " (:border card-colors))
                :borderBottom (str "1px solid " (:border card-colors))
                :borderLeft (str "3px solid " (:accent card-colors))
                :borderRadius "var(--mantine-radius-sm)"
                :color "var(--mantine-color-gray-9)"
                :cursor "pointer"
                :display "flex"
                :flexDirection (if compact? "row" "column")
                :alignItems (if compact? "center" "stretch")
                :gap (if compact? 8 4)
                :overflow "hidden"
                :whiteSpace (when compact? "nowrap")
                :transition "box-shadow 140ms ease"}}
       (if compact?
         ;; Compact single-line: "8:30 · 1-on-1 Tutorial · Mia .......... TRIAL".
         ;;
         ;; The tag is right-aligned here too, for the same reason the stacked
         ;; layout does it: a reader scanning a column of cards should find the
         ;; tag in ONE place. It sat inline next to the time first — it reads
         ;; fine in isolation, delimited by the `·` separators — but next to a
         ;; stacked card whose tag is hard-right, the pair make the eye hunt.
         ;; Consistent position beats the in-sentence reading.
         ;;
         ;; The tag can't simply be the last flex item, though. This row cannot
         ;; wrap — a ≤30-min block has no second line to give — so whatever
         ;; sits at the tail is exactly what `overflow: hidden` clips, which is
         ;; why the tag led originally. `minWidth 0` on the text alone doesn't
         ;; save it either: the time and the `·` separators are unshrinkable,
         ;; and in a 141px week column they plus the tag already exceed the
         ;; card (measured: 4px of the tag outside the edge).
         ;;
         ;; So the time/program/names become one group that shrinks and clips
         ;; as a unit, and the tag is that group's sibling rather than its last
         ;; child. The group can collapse to nothing; the tag, `flexShrink 0`
         ;; outside it, always keeps its place. Within the group the squeeze
         ;; still lands on the attendee names first (shrink 3 vs 1) — a name
         ;; shortening to "Mi…" costs less than a program name doing the same.
         ($ :<>
            ($ :div {:style {:display "flex"
                             :alignItems "center"
                             :gap 8
                             :flexShrink 1
                             :minWidth 0
                             :overflow "hidden"}}
               ($ :span {:style {:fontFamily "Plus Jakarta Sans, sans-serif"
                                 :fontSize 11
                                 :fontWeight 500
                                 :color "var(--mantine-color-gray-7)"
                                 :fontVariantNumeric "tabular-nums"
                                 :flexShrink 0}}
                  (format-time-12h start-time))
               ($ :span {:style {:color "var(--mantine-color-gray-4)" :flexShrink 0}}
                  "·")
               ($ :span {:style {:fontFamily "Sora, sans-serif"
                                 :fontSize 12.5
                                 :fontWeight 600
                                 :color "var(--mantine-color-gray-9)"
                                 :overflow "hidden"
                                 :textOverflow "ellipsis"
                                 :flexShrink 1
                                 :minWidth 0}}
                  program-name)
               (when attendee-label
                 ($ :<>
                    ($ :span {:style {:color "var(--mantine-color-gray-4)" :flexShrink 0}}
                       "·")
                    ($ :span {:style {:fontFamily "Plus Jakarta Sans, sans-serif"
                                      :fontSize 12
                                      :fontWeight 500
                                      :color kinds/card-text-color
                                      :overflow "hidden"
                                      :textOverflow "ellipsis"
                                      :whiteSpace "nowrap"
                                      :flexShrink 3
                                      :minWidth 0}}
                       attendee-label))))
            (when tag
              ($ :div {:style {:marginLeft "auto" :flexShrink 0 :display "flex"}}
                 ($ kind-tag {:tag tag}))))
         ;; Standard stacked layout for blocks ≥60-min.
         ($ :<>
            ;; The tag is pushed to the far end of the time row rather than
            ;; trailing the time. Sitting next to it, "11:30 am TRIAL" reads as
            ;; one run-on phrase — the compact layout gets away with the same
            ;; adjacency only because `·` separators delimit it. Right-aligned,
            ;; the tag has one predictable position on every card and fills
            ;; space that was empty anyway.
            ;;
            ;; `wrap` is what keeps that honest on a narrow card. A concurrent
            ;; block in a week column is ~67px wide, where the time's own
            ;; min-content ("11:30 am" on its third wrapped line) plus the tag
            ;; exceeds the row outright — the tag overflowed the card and
            ;; `overflow: hidden` ate it, leaving an amber card with nothing
            ;; but colour to say why. That is the one failure this tag exists
            ;; to prevent. Wrapping drops it to its own line instead, where it
            ;; is narrow enough to always fit. Measured, not assumed: the tag
            ;; ran 17px past the card's right edge before, 20px inside it now.
            ($ :div {:style {:display "flex"
                             :alignItems "baseline"
                             :justifyContent "space-between"
                             :flexWrap "wrap"
                             :gap 7
                             :fontFamily "Plus Jakarta Sans, sans-serif"
                             :fontSize 11
                             :fontWeight 500
                             :color "var(--mantine-color-gray-7)"
                             :fontVariantNumeric "tabular-nums"}}
               ($ :span {} (format-time-range start-time end-time))
               (when tag
                 ($ kind-tag {:tag tag})))
            ($ :div {:style {:fontFamily "Sora, sans-serif"
                             :fontSize 13.5
                             :fontWeight 600
                             :lineHeight 1.25
                             :color "var(--mantine-color-gray-9)"}}
               program-name)
            (when attendee-label
              ($ :div {:style {:marginTop "auto"
                               :fontFamily "Plus Jakarta Sans, sans-serif"
                               :fontSize 12.5
                               :fontWeight 500
                               :color kinds/card-text-color}}
                 attendee-label)))))))

(defui time-grid
  "Continuous 7am–9pm vertical time grid with absolutely-positioned session
   blocks. Pure layout component.

   The hour rail (7am–9pm) is interpreted in `school-tz` (SMS-49) so
   session blocks land in the correct row regardless of browser tz."
  [{:keys [sessions desktop? school-tz on-session-click]}]
  (let [px-per-min (if desktop? px-per-min-desktop px-per-min-mobile)
        rail-w (if desktop? rail-w-desktop rail-w-mobile)
        inner-height (* hours-in-window 60 px-per-min)]
    ($ :div {:style {:position "relative"
                     :flex 1
                     :overflowY "auto"
                     :padding "14px 0 24px"}}
       ($ :div {:style {:position "relative"
                        :height inner-height}}
          ;; Hour grid lines
          (for [h (range day-start-hour (inc day-end-hour))]
            ($ hour-rail-tick {:key h :hour h :px-per-min px-per-min :rail-w rail-w}))
          ;; Session lane — full available width. Per-block max width is
          ;; enforced inside session-block via min(slot-share, block-max-w).
          ($ :div {:style {:position "absolute"
                           :left rail-w
                           :right (if desktop? 28 18)
                           :top 0
                           :bottom 0}}
             (for [s sessions]
               ($ session-block
                  {:key (:id s)
                   :session s
                   :px-per-min px-per-min
                   :school-tz school-tz
                   :on-click on-session-click})))))))

;; ----------------------------------------------------------------------------
;; Week grid (SMS-153) — seven day-columns sharing the day view's time axis.
;; ----------------------------------------------------------------------------

(defui week-day-column
  "One day-column of the week grid: a day-name header over a continuous
   7am–9pm body with hour gridlines and absolutely-positioned session
   blocks. Blocks reuse the day view's session-block (and its :col /
   :col-count overlap split), so concurrent same-day sessions render
   side-by-side within this column (SMS-153 Slice 3)."
  [{:keys [date sessions today? px-per-min school-tz inner-height col-min-w on-session-click]}]
  (let [weekday (.toLocaleDateString date "en-US" #js {:weekday "short"})]
    ($ :div {:style {:flex (str "1 0 " col-min-w "px")
                     :minWidth col-min-w
                     :display "flex"
                     :flexDirection "column"
                     :borderLeft "1px solid var(--mantine-color-gray-2)"}}
       ;; Day-name header cell.
       ($ :div {:style {:height week-header-h
                        :display "flex"
                        :flexDirection "column"
                        :alignItems "center"
                        :justifyContent "center"
                        :gap 1
                        :borderBottom "1px solid var(--mantine-color-gray-2)"
                        :backgroundColor (if today? "var(--mantine-color-brand-0)" "white")}}
          ($ :span {:style {:fontFamily "Plus Jakarta Sans, sans-serif"
                            :fontSize 11
                            :fontWeight 600
                            :textTransform "uppercase"
                            :letterSpacing "0.04em"
                            :color (if today? "var(--mantine-color-brand-7)"
                                       "var(--mantine-color-gray-6)")}}
             weekday)
          ($ :span {:style {:fontFamily "Sora, sans-serif"
                            :fontSize 16
                            :fontWeight 700
                            :lineHeight 1
                            :fontVariantNumeric "tabular-nums"
                            :color (if today? "var(--mantine-color-brand-7)"
                                       "var(--mantine-color-gray-9)")}}
             (.getDate date)))
       ;; Day body — gridlines + session blocks.
       ($ :div {:style {:position "relative" :height inner-height}}
          (for [h (range day-start-hour (inc day-end-hour))]
            ($ :div {:key h
                     :style {:position "absolute" :left 0 :right 0
                             :top (* (- (* h 60) (* day-start-hour 60)) px-per-min)
                             :height 1
                             :backgroundColor "var(--mantine-color-gray-1)"}}))
          (for [s sessions]
            ($ session-block
               {:key (:id s)
                :session s
                :px-per-min px-per-min
                :school-tz school-tz
                :on-click on-session-click}))))))

(defui week-grid
  "Week-view layout: a horizontally-scrollable row of a time rail plus seven
   Mon–Sun day-columns, all sharing the day view's 7am–9pm axis, px-per-min,
   and session-block styling.

   `sessions-by-day` is a map {YYYY-MM-DD -> [sessions]} — a session's day is
   its date in `school-tz`, so a late-evening Manila session stays on its
   Manila column regardless of the browser tz. `days` is the 7 Mon..Sun
   js/Dates from cal/week-days.

   On a phone the columns keep a minimum width, so the 7-column row overflows
   and scrolls horizontally (day view stays the mobile default)."
  [{:keys [days sessions-by-day desktop? school-tz on-session-click]}]
  (let [px-per-min (if desktop? px-per-min-desktop px-per-min-mobile)
        rail-w (if desktop? rail-w-desktop rail-w-mobile)
        col-min-w (if desktop? week-col-min-w-desktop week-col-min-w-mobile)
        inner-height (* hours-in-window 60 px-per-min)
        today-ymd (cal/format-ymd (today-in-tz school-tz))]
    ($ :div {:style {:position "relative"
                     :flex 1
                     :overflow "auto"
                     :padding "0 0 24px"}}
       ($ :div {:style {:display "flex" :alignItems "stretch"}}
          ;; Time rail — hour labels aligned to the shared axis.
          ($ :div {:style {:width rail-w
                           :flexShrink 0
                           :display "flex"
                           :flexDirection "column"}}
             ($ :div {:style {:height week-header-h
                              :borderBottom "1px solid var(--mantine-color-gray-2)"}})
             ($ :div {:style {:position "relative" :height inner-height}}
                (for [h (range day-start-hour (inc day-end-hour))]
                  (let [[n suffix] (hour-label h)
                        top-px (* (- (* h 60) (* day-start-hour 60)) px-per-min)]
                    ($ :span {:key h
                              :style {:position "absolute"
                                      :right 8
                                      :top (- top-px 7)
                                      :fontFamily "Plus Jakarta Sans, sans-serif"
                                      :fontSize 11 :fontWeight 500
                                      :color "var(--mantine-color-gray-6)"
                                      :fontVariantNumeric "tabular-nums"
                                      :whiteSpace "nowrap"}}
                       n
                       ($ :span {:style {:color "var(--mantine-color-gray-5)"
                                         :marginLeft 2 :fontWeight 400}}
                          suffix))))))
          ;; Seven day-columns.
          (for [d days]
            (let [ymd (cal/format-ymd d)]
              ($ week-day-column
                 {:key ymd
                  :date d
                  :sessions (get sessions-by-day ymd [])
                  :today? (= ymd today-ymd)
                  :px-per-min px-per-min
                  :school-tz school-tz
                  :inner-height inner-height
                  :col-min-w col-min-w
                  :on-session-click on-session-click})))))))

(defui view-toggle
  "Day / Week segmented control. Reflects the current `view` keyword and
   reports the chosen one via on-change. The view also lives in the URL
   (?view=week) — calendar-page is the source of truth."
  [{:keys [view on-change]}]
  ($ ui/segmented-control
     {:value (name view)
      :onChange (fn [v] (on-change (keyword v)))
      :size "sm"
      :radius "xl"
      :data (clj->js [{:label "Day" :value "day"}
                      {:label "Week" :value "week"}])}))

(defui day-header
  "Page header above the card. Sora day title with day-nav (‹ Today ›)
   inline beside it so the controls sit next to the date they change.
   Meta line (session count) below; primary action (+ Session) stays
   right-aligned.

   When the browser tz differs from the school tz (SMS-49), a subtle
   pill 'Times in <school-tz>' is shown next to the date so cross-tz
   admins know times are wall-clock in the school's tz, not theirs."
  [{:keys [date sessions-count school-tz view on-view-change
           on-prev on-today on-next on-new-session]}]
  (let [b-tz (browser-tz)
        cross-tz? (and school-tz b-tz (not= school-tz b-tz))]
    ($ ui/group {:justify "space-between"
                 :align "flex-start"
                 :wrap "wrap"
                 :gap "md"}
       ($ ui/stack {:gap 4 :style {:flex 1 :minWidth 0}}
          ($ ui/group {:gap "md" :align "center" :wrap "wrap"}
             ;; Google-Calendar pattern: nav controls anchored LEFT, date label
             ;; follows. The date's width can change freely without pushing the
             ;; chevrons — mouse stays parked over a chevron between clicks.
             ($ ui/group {:gap "xs" :wrap "nowrap"}
                ($ ui/action-icon {:variant "subtle" :radius "xl"
                                   :aria-label "Previous day"
                                   :onClick on-prev}
                   ($ icons/icon-chevron-left {:size 16 :stroke 1.7}))
                ($ ui/button {:variant "default" :size "sm" :radius "xl"
                              :onClick on-today}
                   "Today")
                ($ ui/action-icon {:variant "subtle" :radius "xl"
                                   :aria-label "Next day"
                                   :onClick on-next}
                   ($ icons/icon-chevron-right {:size 16 :stroke 1.7})))
             ($ ui/title {:order 2
                          :style {:fontFamily "Sora, sans-serif"
                                  :fontWeight 700
                                  :letterSpacing "-0.015em"
                                  :lineHeight 1.15
                                  :fontVariantNumeric "tabular-nums"}}
                (day-label date))
             ;; SMS-49 — subtle tz pill. Only visible when the admin's
             ;; browser tz ≠ the school's tz; otherwise the calendar's
             ;; times match their wall-clock and the badge is just noise.
             (when cross-tz?
               ($ ui/badge {:variant "light"
                            :color "gray"
                            :size "sm"
                            :radius "xl"
                            :title (str "Browser tz: " b-tz)}
                  (str "Times in " school-tz))))
          ($ ui/text {:size "sm" :c "dimmed"}
             (str sessions-count " session" (when-not (= 1 sessions-count) "s"))))
       ($ ui/group {:gap "sm" :wrap "nowrap"}
          ($ view-toggle {:view view :on-change on-view-change})
          ($ ui/button {:variant "filled" :color "brand" :radius "xl"
                        :leftSection ($ icons/icon-plus {:size 14})
                        :onClick on-new-session}
             "Session")))))

(defui week-header
  "Week-view page header: prev/today/next week-nav anchored left, a
   natural-language week-range title (cal/week-range-label) beside it, the
   Day/Week toggle and the primary New Session action on the right. Mirrors
   day-header so day↔week feels consistent; prev/next step a whole week."
  [{:keys [days sessions-count school-tz view on-view-change
           on-prev on-today on-next on-new-session]}]
  (let [b-tz (browser-tz)
        cross-tz? (and school-tz b-tz (not= school-tz b-tz))]
    ($ ui/group {:justify "space-between"
                 :align "flex-start"
                 :wrap "wrap"
                 :gap "md"}
       ($ ui/stack {:gap 4 :style {:flex 1 :minWidth 0}}
          ($ ui/group {:gap "md" :align "center" :wrap "wrap"}
             ;; Nav controls anchored LEFT (same as day-header): the week label
             ;; can change width without shifting the chevrons.
             ($ ui/group {:gap "xs" :wrap "nowrap"}
                ($ ui/action-icon {:variant "subtle" :radius "xl"
                                   :aria-label "Previous week"
                                   :onClick on-prev}
                   ($ icons/icon-chevron-left {:size 16 :stroke 1.7}))
                ($ ui/button {:variant "default" :size "sm" :radius "xl"
                              :onClick on-today}
                   "Today")
                ($ ui/action-icon {:variant "subtle" :radius "xl"
                                   :aria-label "Next week"
                                   :onClick on-next}
                   ($ icons/icon-chevron-right {:size 16 :stroke 1.7})))
             ($ ui/title {:order 2
                          :style {:fontFamily "Sora, sans-serif"
                                  :fontWeight 700
                                  :letterSpacing "-0.015em"
                                  :lineHeight 1.15
                                  :fontVariantNumeric "tabular-nums"}}
                (cal/week-range-label days))
             (when cross-tz?
               ($ ui/badge {:variant "light"
                            :color "gray"
                            :size "sm"
                            :radius "xl"
                            :title (str "Browser tz: " b-tz)}
                  (str "Times in " school-tz))))
          ($ ui/text {:size "sm" :c "dimmed"}
             (str sessions-count " session" (when-not (= 1 sessions-count) "s"))))
       ($ ui/group {:gap "sm" :wrap "nowrap"}
          ($ view-toggle {:view view :on-change on-view-change})
          ($ ui/button {:variant "filled" :color "brand" :radius "xl"
                        :leftSection ($ icons/icon-plus {:size 14})
                        :onClick on-new-session}
             "Session")))))

;; ============================================================================
;; Data fetching (container)
;; ============================================================================

(defn- fetch-sessions
  "GET /api/sessions bounded to the given day, scoped to the active
   branch. Calls on-success with the raw response body.

   Sends a single `?date=YYYY-MM-DD` rather than two ISO instants — since
   #590 sessions carry a naive wall clock, so this matches the `date` column
   directly with no tz expansion. Routes through api/request! so base URL +
   auth + the shared 401 policy are applied (a 401 now triggers the
   configured logout flow)."
  [^js date branch-id on-success on-error]
  (api/request!
   {:method :get
    :path "/sessions"
    :params (cond-> (assoc (cal/day-params date) :page-size 100)
              branch-id (assoc :branch-id branch-id))
    :on-success on-success
    :on-error on-error}))

(defn- fetch-sessions-range
  "GET /api/sessions bounded to a [from..to] day range (inclusive of both
   wall-dates), scoped to the active branch — the week-view counterpart of
   fetch-sessions (SMS-153). One request covers the whole week, as
   `?date-from=A&date-to=B` against the aggregate's `:date` date-range filter
   (see cal/week-params for why those exact param names)."
  [^js from-date ^js to-date branch-id on-success on-error]
  (api/request!
   {:method :get
    :path "/sessions"
    :params (cond-> (assoc (cal/week-params from-date to-date) :page-size 100)
              branch-id (assoc :branch-id branch-id))
    :on-success on-success
    :on-error on-error}))

(defn- normalize-session
  "Flatten the API response shape into what the layout needs.
   :attendees-count, :attendees-preview and :attendee-kinds are provided by
   the Session :after-read enrichment. Preview is a vec of up to 2 first
   names — the block renders names if ≤2 attendees, count otherwise.

   #512: :attendee-kinds stays a vector rather than being reduced to a single
   dominant kind here. The block needs both ends of it — `first` for the
   colour, the count for the mixed marker — and flattening it at the boundary
   would put those two facts in different places."
  [s]
  {:id (:id s)
   :date (:date s)
   :start-time (:start-time s)
   :end-time (:end-time s)
   :branch-name (or (get-in s [:branch :name]) nil)
   :program (or (:program s) {:name "Session"})
   :attendee-count (or (:attendees-count s) 0)
   :attendees-preview (or (:attendees-preview s) [])
   :attendee-kinds (or (:attendee-kinds s) [])})

(defn- shift-day [^js d delta-days]
  (js/Date. (.getFullYear d) (.getMonth d) (+ (.getDate d) delta-days) 0 0 0 0))

(defui calendar-page
  "Sessions calendar container. The active view (:day | :week) is derived
   from the URL (?view=week, default :day) so it survives refresh and is
   shareable; `date` is the anchor — the shown day, or any day in the shown
   week. The day view pulls one ?date; the week view pulls the whole Mon–Sun
   window in one ?date-from/?date-to request and lays it out per day-column."
  []
  (let [route (urf/use-subscribe [::router/current-route])
        view (if (= "week" (get-in route [:query-params :view])) :week :day)
        [date set-date!] (use-state #(js/Date.))
        [items set-items!] (use-state [])
        [loading? set-loading!] (use-state true)
        [error set-error!] (use-state nil)
        [is-desktop? set-desktop!] (use-state
                                    #(and (exists? js/window)
                                          (>= (.-innerWidth js/window) 768)))
        branch-id (urf/use-subscribe [::ac.subs/branch-id])
        ;; SMS-49 — single source of truth for the school's IANA tz. All
        ;; time-axis rendering, the Today button, and the pill use this.
        school-tz (urf/use-subscribe [::ac.subs/school-timezone])
        ;; #715 — lowercase day names the active context operates on, or nil
        ;; when unconfigured, which renders the full week.
        open-days (urf/use-subscribe [::ac.subs/open-day-names])
        ;; Fetch sessions on view: the day view pulls one ?date, the week
        ;; view the whole Mon–Sun window in one ?date-from/?date-to request. Stored
        ;; `items` is the flat normalized list — overlap layout is applied at
        ;; render (per day-column for the week, see cal/layout-week).
        ;;
        ;; SMS-272: always fetch — never gate on branch-id. A nil branch-id is a
        ;; legitimate "All Branches" state (single-branch schools + multi-branch
        ;; admins who haven't picked); fetch-sessions/-range omit the param when
        ;; nil, and the server scope returns the whole school, so this renders
        ;; all-school sessions instead of a blank grid.
        reload (use-callback
                (fn [^js d]
                  (set-error! nil)
                  (let [on-ok (fn [resp]
                                (set-items! (->> (:items resp)
                                                 (mapv normalize-session)
                                                 vec))
                                (set-loading! false))
                        on-err (fn [_]
                                 (set-error! "Failed to load sessions")
                                 (set-loading! false))]
                    (set-loading! true)
                    (if (= view :week)
                      (let [days (cal/week-days d)]
                        (fetch-sessions-range (first days) (last days)
                                              branch-id on-ok on-err))
                      (fetch-sessions d branch-id on-ok on-err))))
                [branch-id view])
        ;; Refetch on date OR branch change (reload closes over branch-id,
        ;; so changing branch produces a new reload, re-running this effect)
        _ (use-effect
           (fn []
             (reload date)
             js/undefined)
           [date reload])
        ;; Track viewport for desktop vs mobile sizing
        _ (use-effect
           (fn []
             (when (exists? js/window)
               (let [on-resize (fn []
                                 (set-desktop! (>= (.-innerWidth js/window) 768)))]
                 (.addEventListener js/window "resize" on-resize)
                 #(.removeEventListener js/window "resize" on-resize))))
           [])
        ;; The view is URL-driven: toggling navigates (?view=week / cleared),
        ;; which re-derives `view` above. calendar-page stays the source of
        ;; truth; the segmented control just reflects it.
        on-view-change (use-callback
                        (fn [v]
                          (router/navigate! :calendar {}
                                            (when (= v :week) {:view "week"})))
                        [])
        ;; Prev/next step by a day in the day view and by a week (cal/shift-week,
        ;; ±7) in the week view; the anchor `date` is shared by both.
        on-prev (use-callback
                 #(set-date! ((if (= view :week) cal/shift-week shift-day) date -1))
                 [date view])
        on-next (use-callback
                 #(set-date! ((if (= view :week) cal/shift-week shift-day) date 1))
                 [date view])
        ;; SMS-49 — Today means *school's*-today, not browser's-today, so
        ;; a Tokyo admin viewing a Manila school still jumps to Manila
        ;; today (could be the day before in Tokyo wall-clock terms). In the
        ;; week view this lands on the week containing the school's today.
        on-today (use-callback #(set-date! (today-in-tz school-tz))
                               [school-tz])
        ;; Slide-over state: nil = closed, {:mode :create} = new session,
        ;; {:mode :edit :id <uuid>} = editing an existing session.
        ;; Embeds the custom session-edit/session-form so the create/edit
        ;; flow stays in-place (calendar context preserved) and uses a
        ;; date+time+duration model instead of two raw ISO timestamps.
        [slide set-slide!] (use-state nil)
        on-new-session (use-callback #(set-slide! {:mode :create}) [])
        on-session-click (use-callback
                          (fn [id] (set-slide! {:mode :edit :id id}))
                          [])
        on-slide-close (use-callback
                        (fn []
                          (set-slide! nil)
                          ;; Refresh the day so newly-created / edited
                          ;; sessions show up without a manual reload.
                          (reload date))
                        [reload date])
        on-slide-cancel (use-callback #(set-slide! nil) [])
        ;; #715 — computed once and shared by the header and the grid so the
        ;; range label always describes the columns actually on screen.
        ;; Navigation is unaffected: prev/next step the anchor `date` by a
        ;; whole week (cal/shift-week), not by the number of visible columns.
        week-sessions (when (= view :week) (cal/layout-week items))
        visible-days (when (= view :week)
                       (cal/visible-week-days (cal/week-days date)
                                              week-sessions
                                              open-days))]
    ($ :<>
       ($ ui/stack {:gap "md"}
          (if (= view :week)
            ($ week-header
               {:days visible-days
                :sessions-count (count items)
                :school-tz school-tz
                :view view
                :on-view-change on-view-change
                :on-prev on-prev
                :on-today on-today
                :on-next on-next
                :on-new-session on-new-session})
            ($ day-header
               {:date date
                :sessions-count (count items)
                :school-tz school-tz
                :view view
                :on-view-change on-view-change
                :on-prev on-prev
                :on-today on-today
                :on-next on-next
                :on-new-session on-new-session}))
          ($ ui/paper {:p "md" :withBorder true :shadow "xs" :radius "md"
                       :style {:height "calc(100vh - 220px)"
                               :minHeight 520
                               :display "flex"
                               :flexDirection "column"}}
             (cond
               loading?
               ($ ui/center {:style {:flex 1}} ($ ui/loader))

               error
               ($ ui/center {:style {:flex 1}}
                  ($ ui/text {:c "red"} error))

               (= view :week)
               ($ week-grid
                  {:days visible-days
                   :sessions-by-day week-sessions
                   :desktop? is-desktop?
                   :school-tz school-tz
                   :on-session-click on-session-click})

               :else
               ($ time-grid
                  {:sessions (cal/assign-overlap-layout items)
                   :desktop? is-desktop?
                   :school-tz school-tz
                   :on-session-click on-session-click}))))
       ;; Session create/edit slide-over — uses the custom session-form
       ;; with date+time+duration model. default-date pre-fills create
       ;; mode to whatever day the calendar is currently showing.
       ($ slide-over/slide-over
          {:open? (some? slide)
           :on-close on-slide-cancel
           :title (case (:mode slide)
                    :create "New Session"
                    :edit "Edit Session"
                    "")}
          (when slide
            ($ session-edit/session-form
               {:mode (:mode slide)
                :session-id (:id slide)
                :default-date date
                :on-saved on-slide-close
                :on-cancel on-slide-cancel}))))))
